import {
  addDoc,
  collection,
  doc,
  DocumentData,
  DocumentReference,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './config';

export type ContestantStatus = 'pending' | 'verified' | 'rejected';

export interface Contestant {
  id?: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  nationality?: string;
  origin?: string;
  residence?: string;
  bio: string;
  image: string;
  status: ContestantStatus;
  votes: number;
  createdAt?: Timestamp | null;
}

export interface Vote {
  id?: string;
  contestantId: string;
  voterEmail: string;
  voterName: string;
  voterPhone: string;
  createdAt?: Timestamp | null;
}

export interface VoterSubmission {
  email: string;
  name: string;
  phone: string;
}

export interface SeasonResetResult {
  deletedContestants: number;
  deletedVotes: number;
}

const normalizeEmail = (email: string) => email.trim().toLowerCase();
const normalizePhone = (phone: string) => phone.replace(/\s+/g, '').trim();

const toMillis = (value?: Timestamp | null) => {
  if (!value) {
    return 0;
  }

  if (typeof (value as Timestamp).toMillis === 'function') {
    return (value as Timestamp).toMillis();
  }

  return 0;
};

const mapContestant = (snapshot: any): Contestant => ({
  id: snapshot.id,
  ...snapshot.data(),
});

const sortContestantsByCreatedAt = (contestants: Contestant[]) =>
  [...contestants].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

const MAX_BATCH_OPERATIONS = 450;

export const sortContestantsByVotes = (contestants: Contestant[]) =>
  [...contestants].sort((a, b) => (b.votes || 0) - (a.votes || 0));

const deleteDocumentRefs = async (refs: DocumentReference<DocumentData>[]) => {
  for (let index = 0; index < refs.length; index += MAX_BATCH_OPERATIONS) {
    const batch = writeBatch(db);
    refs.slice(index, index + MAX_BATCH_OPERATIONS).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
};

export const createContestant = async (
  data: Omit<Contestant, 'id' | 'status' | 'votes' | 'createdAt'>
) => {
  return await addDoc(collection(db, 'contestants'), {
    ...data,
    status: 'pending',
    votes: 0,
    createdAt: serverTimestamp(),
  });
};

export const getContestant = async (id: string) => {
  const docRef = doc(db, 'contestants', id);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? (mapContestant(docSnap) as Contestant) : null;
};

export const getAllContestants = async () => {
  const querySnapshot = await getDocs(collection(db, 'contestants'));
  return sortContestantsByCreatedAt(querySnapshot.docs.map(mapContestant));
};

export const getVerifiedContestants = async () => {
  const q = query(collection(db, 'contestants'), where('status', '==', 'verified'));
  const querySnapshot = await getDocs(q);
  return sortContestantsByCreatedAt(querySnapshot.docs.map(mapContestant));
};

export const subscribeToAllContestants = (
  onData: (contestants: Contestant[]) => void,
  onError?: (error: Error) => void
) =>
  onSnapshot(
    collection(db, 'contestants'),
    (snapshot) => {
      onData(sortContestantsByCreatedAt(snapshot.docs.map(mapContestant)));
    },
    (error) => onError?.(error)
  );

export const subscribeToVerifiedContestants = (
  onData: (contestants: Contestant[]) => void,
  onError?: (error: Error) => void
) =>
  onSnapshot(
    query(collection(db, 'contestants'), where('status', '==', 'verified')),
    (snapshot) => {
      onData(sortContestantsByCreatedAt(snapshot.docs.map(mapContestant)));
    },
    (error) => onError?.(error)
  );

export const updateContestantStatus = async (id: string, status: ContestantStatus) => {
  const docRef = doc(db, 'contestants', id);
  return await updateDoc(docRef, { status });
};

export const deleteContestantRegistration = async (contestantId: string) => {
  const contestantRef = doc(db, 'contestants', contestantId);
  const contestantVotes = await getDocs(
    query(collection(db, 'votes'), where('contestantId', '==', contestantId))
  );

  await deleteDocumentRefs([contestantRef, ...contestantVotes.docs.map((snapshot) => snapshot.ref)]);

  return {
    deletedContestant: 1,
    deletedVotes: contestantVotes.size,
  };
};

export const clearCurrentVotingSeason = async (): Promise<SeasonResetResult> => {
  const [contestantSnapshot, voteSnapshot] = await Promise.all([
    getDocs(collection(db, 'contestants')),
    getDocs(collection(db, 'votes')),
  ]);

  await deleteDocumentRefs([
    ...contestantSnapshot.docs.map((snapshot) => snapshot.ref),
    ...voteSnapshot.docs.map((snapshot) => snapshot.ref),
  ]);

  return {
    deletedContestants: contestantSnapshot.size,
    deletedVotes: voteSnapshot.size,
  };
};

export const castVote = async (contestantId: string, voterData: VoterSubmission) => {
  const voterEmail = normalizeEmail(voterData.email);
  const voterPhone = normalizePhone(voterData.phone);
  const voterName = voterData.name.trim();

  if (!voterEmail || !voterPhone || !voterName) {
    const error = new Error('Missing voter details.');
    (error as Error & { code?: string }).code = 'invalid-vote';
    throw error;
  }

  const voteRef = doc(db, 'votes', voterEmail);
  const contestantRef = doc(db, 'contestants', contestantId);
  const batch = writeBatch(db);

  batch.set(voteRef, {
    contestantId,
    voterEmail,
    voterName,
    voterPhone,
    createdAt: serverTimestamp(),
  });
  batch.update(contestantRef, { votes: increment(1) });

  await batch.commit();
};

export const getVotesForContestant = async (contestantId: string) => {
  const q = query(collection(db, 'votes'), where('contestantId', '==', contestantId));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((snapshot) => ({ id: snapshot.id, ...snapshot.data() } as Vote));
};
